'use client';

import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@ahafood/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from '@ahafood/ui/components/form';
import { Image } from '@ahafood/ui/components/image';
import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import { Sheet, SheetContent, SheetTitle } from '@ahafood/ui/components/sheet';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { Textarea } from '@ahafood/ui/components/textarea';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn, formatCurrency } from '@ahm/common-helpers';
import { getCookie } from 'cookies-next';
import { useRouter } from 'next/navigation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CustomizationGroup } from './custom-components/customization-group';
import { ItemHeader } from './custom-components/item-header';
import { QuantityControl } from './custom-components/quantity-control';
import type { StoreContext } from './store-item-sheet-context';
import { useCustomizationForm } from '@/api/use-customization-form';
import { useGetMenuWS, type SoloMeal } from '@/api/use-get-menu-ws';
import { useGetOrderCustomizationWS } from '@/api/use-get-order-customization-ws';
import { useQuantityControl } from '@/api/use-quantity-control';
import { useNavigation } from '@/components/navigation/navigation-context';
import { ROUTES } from '@/config/router';
import { useBottomPosition } from '@/hooks/use-bottom-position';
import { useStoreContextValidation } from '@/hooks/use-store-context-validation';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile';
import { storeSoloMealToMenuItem } from '@/lib/item-converters';
import { useAuthStore } from '@/store';
import { useCartStore } from '@/store/cart/cart-store-provider';
import type { MenuItem } from '@/types/item';

interface Props {
  item: MenuItem | null;
  onClose: () => void;
  // Tham số tùy chọn để xử lý store context từ external sources
  storeContext?: StoreContext;
}

export function StoreItemSheet({ item, onClose, storeContext }: Props) {
  const router = useRouter();
  const token = String(getCookie('ws_token') ?? '');
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  // const cityId = useAiFoodOrderStore.use.city_id();
  const { bottomPosition } = useBottomPosition();

  // State to track current item (allows switching between solo meals)
  const [currentItem, setCurrentItem] = useState<MenuItem | null>(item);

  // Fetch menu data when opened from SolomealContainer
  const shouldFetchMenu = Boolean(
    storeContext?.tag === 'solomeal' &&
      storeContext?.storeId &&
      !isNaN(Number(storeContext.storeId))
  );

  const { data: menuData, isLoading: isLoadingMenu } = useGetMenuWS(
    Number(storeContext?.storeId ?? 0),
    {
      enabled: shouldFetchMenu,
    }
  );

  const soloMeals = useMemo(
    () =>
      (menuData?.solo_meals ?? []).filter((meal) => meal.status !== 'INACTIVE'),
    [menuData?.solo_meals]
  );

  // Update currentItem when item prop changes
  useEffect(() => {
    if (item) {
      setCurrentItem(item);
    }
  }, [item]);

  const { validateAndSetStoreContext, needsValidation } =
    useStoreContextValidation({
      item: currentItem,
      pageId: storeContext?.pageId,
      slug: storeContext?.slug,
      branchId: storeContext?.branchId,
    });

  const queryParams = useMemo(
    () => ({ item_id: currentItem?._id ?? 0 }),
    [currentItem?._id]
  );

  const wsQuery = useGetOrderCustomizationWS(token, queryParams, {
    enabled: !!currentItem,
  });

  const { data, isLoading, refetch } = wsQuery;

  const customizations = useMemo(
    () => data?.customizations ?? [],
    [data?.customizations]
  );

  const {
    form,
    formQuantity,
    handleSubmit,
    getSelectedCustomizations,
    validateCustomizations,
    calculateTotalPrice,
    setValue,
  } = useCustomizationForm({ item: currentItem, customizations });

  const { isMaxQuantity, increaseQuantity, decreaseQuantity } =
    useQuantityControl({
      item: currentItem,
      formQuantity,
      setValue,
    });

  const storeSlug = useCartStore((state) => state.storeSlug);
  const pageId = useCartStore((state) => state.pageId);
  const branchId = useCartStore((state) => state.branchId);
  const addToCart = useCartStore((state) => state.addToCart);
  const updateItemInCart = useCartStore((state) => state.updateItemInCart);

  useEffect(() => {
    setOpen(!!item);
    if (currentItem?._id) {
      void refetch();
    }
  }, [item, currentItem, refetch]);

  const handleOpenChange = useCallback(
    (value: boolean) => {
      if (!value) {
        const isInTransition =
          document.documentElement.dataset.drawerToSheetTransition === 'true';
        if (!isInTransition) {
          onClose();
        }
      }
      setOpen(value);
    },
    [onClose]
  );

  // Handler for clicking on a solo meal item in the carousel
  const handleSoloMealClick = useCallback(
    (soloMeal: SoloMeal) => {
      // Convert SoloMeal to MenuItem
      const menuItem = storeSoloMealToMenuItem(soloMeal);

      // Update current item
      setCurrentItem(menuItem);

      // Track click event
      trackEvent('click_button', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: 'solo_meal_carousel_item',
        button_location: 'store_item_sheet',
        store_id: storeContext?.storeId ?? '',
        user_id: user?.cunstomerId ?? '',
        item_id: soloMeal._id,
        item_name: soloMeal.name,
        previous_item_id: currentItem?._id,
        tag: storeContext?.tag,
      });
    },
    [storeContext, user, currentItem]
  );

  const onSubmit = useCallback(
    (data: any, e?: React.BaseSyntheticEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (!currentItem || !validateCustomizations()) return;

      // Skip validation cho solomeal tag vì context đã được cung cấp đầy đủ từ external source
      const shouldSkipValidation = storeContext?.tag === 'solomeal';

      // Kiểm tra và thiết lập store context trước khi thêm vào cart (chỉ khi không phải solomeal)
      if (!shouldSkipValidation && needsValidation()) {
        const isValid = validateAndSetStoreContext();
        if (!isValid) {
          console.error('Failed to validate store context');
          // Có thể hiển thị toast error hoặc xử lý lỗi khác tại đây
          return;
        }
      }

      // Keep the structured format for cart
      const selectedCustomizations = getSelectedCustomizations();
      const currentTotalPrice = calculateTotalPrice();

      const updatedItem = {
        _id: currentItem._id,
        name: currentItem.name,
        price: currentItem.price,
        quantity: data.quantity,
        customizations: selectedCustomizations,
        note: data.note,
        tag: storeContext?.tag,
        discounted_price: currentItem.discounted_price, // Lưu giá sau giảm để tính discount
      };

      const cartItemId = (currentItem as any).cartItemId;
      if (cartItemId) {
        updateItemInCart(cartItemId, updatedItem);
      } else {
        addToCart(updatedItem);
      }

      // Track thêm vào giỏ hàng event
      trackEvent('add_to_cart', {
        action_time: Math.floor(Date.now() / 1000),
        item_name: currentItem.name,
        item_id: currentItem._id,
        quantity: data.quantity,
        customizations: JSON.stringify(selectedCustomizations),
        total_price: currentTotalPrice,
        store_id: storeContext?.storeId ?? storeSlug,
        store_slug: storeSlug,
        page_id: pageId,
        // eta: etaMinutes(cityId ?? undefined, item.),
        branch_id: branchId,
        user_id: user?.cunstomerId,
        ...(storeContext?.tag && { tag: storeContext.tag }),
        // Spread metadata để GA log từng field riêng lẻ (không nest object)
        ...(storeContext?.metadata ?? {}),
      });

      // Navigate to cart page if opened from SolomealContainer
      if (storeContext?.tag === 'solomeal' && storeContext?.storeId) {
        // Track navigation event
        trackEvent('click_button', {
          action_time: Math.floor(Date.now() / 1000),
          button_name: 'add_to_cart_and_navigate',
          from_location: 'store_item_sheet',
          to_location: 'order_cart',
          store_id: storeContext.storeId,
          tag: storeContext.tag,
        });

        // Navigate to cart page
        router.push(ROUTES.orderCart(storeContext.storeId));
        closeSheet();
      } else {
        // Default behavior: close sheet
        handleOpenChange(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentItem,
      validateCustomizations,
      needsValidation,
      validateAndSetStoreContext,
      getSelectedCustomizations,
      calculateTotalPrice,
      handleOpenChange,
      updateItemInCart,
      addToCart,
      storeContext,
      router,
    ]
  );

  const closeSheet = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  const totalPrice = useMemo(
    () => calculateTotalPrice(),
    [calculateTotalPrice]
  );

  if (!currentItem) return null;

  // Desktop Dialog version
  if (!isMobile) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="max-w-xl max-h-[90vh] p-0 overflow-hidden"
          data-testid="customize-screen"
        >
          <Form {...form}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col h-full"
            >
              <DialogHeader className="p-4 border-b">
                <DialogTitle className="text-xl font-semibold">
                  {currentItem.name}
                </DialogTitle>
              </DialogHeader>

              <ScrollArea className="flex-1 p-4 max-h-[500px] overflow-auto">
                <div className="space-y-6">
                  {/* Solo Meal Carousel - Desktop */}
                  {shouldFetchMenu && soloMeals.length > 0 && (
                    <SoloMealCarousel
                      soloMeals={soloMeals}
                      currentItemId={currentItem._id}
                      onItemClick={handleSoloMealClick}
                      isLoading={isLoadingMenu}
                    />
                  )}

                  {/* Item details */}
                  <div className="flex gap-4">
                    {currentItem.image_thumb_url && (
                      <div className="size-32 shrink-0 rounded-lg overflow-hidden">
                        <img
                          src={currentItem.image_thumb_url}
                          alt={currentItem.name}
                          className="size-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex md:flex-col justify-between flex-1">
                      <h3 className="text-lg font-semibold text-neutral-90 mb-2 line-clamp-2">
                        {currentItem.name}
                      </h3>
                      {currentItem.description && (
                        <p className="text-sm text-neutral-70 mb-3">
                          {currentItem.description}
                        </p>
                      )}
                      <div className="flex flex-col md:flex-row text-lg font-bold text-primary-50 md:items-center">
                        {formatCurrency(
                          currentItem.discounted_price ?? currentItem.price
                        )}
                        {currentItem.discounted_price && (
                          <span className="ml-2 text-sm text-neutral-50 line-through">
                            {formatCurrency(currentItem.price)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {isLoading ? (
                    <LoadingSkeleton />
                  ) : (
                    <>
                      {customizations.map((customization) => (
                        <CustomizationGroup
                          key={customization._id}
                          customization={customization}
                          control={form.control}
                        />
                      ))}
                      <NoteSection control={form.control} />
                    </>
                  )}
                </div>
              </ScrollArea>

              <div className="p-4 border-t bg-neutral-5 shadow-8-revert">
                <div className="flex items-center gap-4">
                  <QuantityControl
                    control={form.control}
                    quantity={formQuantity}
                    isMaxQuantity={isMaxQuantity}
                    onIncrease={increaseQuantity}
                    onDecrease={decreaseQuantity}
                  />
                  <Button
                    variant="default"
                    size="lg"
                    className="flex-1 py-3 font-bold"
                    type="submit"
                    disabled={isLoading}
                    data-testid="customize-add-to-cart"
                  >
                    <span>Thêm vào giỏ hàng:</span>
                    <span className="ml-2" data-testid="customize-total-price">
                      {formatCurrency(totalPrice)}
                    </span>
                  </Button>
                </div>
                {isMaxQuantity && (
                  <div className="mt-2 text-center text-red-50 text-sm">
                    Đã đạt số lượng tối đa cho phép trong kho.
                  </div>
                )}
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    );
  }

  // Mobile Sheet version

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        className={`size-full max-w-lg mx-auto p-0 [&>button]:data-[state=open]:hidden bg-neutral-10 flex flex-col ios-webview-fix`}
        side="bottom"
        style={{
          WebkitOverflowScrolling: 'touch',
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="customize-screen"
      >
        <SheetTitle className="text-xl font-semibold hidden">
          {currentItem.name}
        </SheetTitle>
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ScrollArea
              className={`w-full h-[calc(100svh-76px-env(safe-area-inset-bottom))]`}
              style={{
                paddingBottom: bottomPosition,
              }}
            >
              <ItemHeader
                item={currentItem}
                storeImageUrl={''}
                onClose={closeSheet}
              />

              {/* Solo Meal Carousel - Mobile */}
              {shouldFetchMenu && soloMeals.length > 0 && (
                <div className="px-4 py-3 bg-white w-full flex items-center justify-between mx-auto">
                  <SoloMealCarousel
                    soloMeals={soloMeals}
                    currentItemId={currentItem._id}
                    onItemClick={handleSoloMealClick}
                    isLoading={isLoadingMenu}
                  />
                </div>
              )}

              {isLoading ? (
                <LoadingSkeleton />
              ) : (
                <>
                  {customizations.map((customization) => (
                    <CustomizationGroup
                      key={customization._id}
                      customization={customization}
                      control={form.control}
                    />
                  ))}

                  <NoteSection control={form.control} />
                </>
              )}
            </ScrollArea>

            <BottomActions
              isLoading={isLoading}
              control={form.control}
              quantity={formQuantity}
              isMaxQuantity={isMaxQuantity}
              onIncrease={increaseQuantity}
              onDecrease={decreaseQuantity}
              totalPrice={totalPrice}
            />
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}

function LoadingSkeleton() {
  return (
    <div className="py-4">
      {[...Array(2)].map((_, index) => (
        <div key={index} className="mb-4 rounded-md overflow-hidden">
          <div className="flex flex-row items-center gap-2 px-4 py-2 text-sm font-semibold justify-between">
            <Skeleton className="h-4 w-1/4 bg-neutral-15" />
            <Skeleton className="h-4 w-1/5 bg-neutral-15" />
          </div>
          {[...Array(3)].map((_, optionIndex) => (
            <div
              key={optionIndex}
              className="flex flex-row items-center gap-3 p-4 w-full bg-white border-b border-neutral-10"
            >
              <Skeleton className="size-4 rounded-full" />
              <Skeleton className="h-4 w-3/5" />
              <Skeleton className="h-4 w-16 ml-auto" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function NoteSection({ control }: { control: any }) {
  return (
    <div className="mb-4">
      <div className="flex flex-row items-center gap-2 md:px-0 px-4 py-2 text-sm font-semibold">
        <h2 className="text-neutral-90 flex-1">Thêm lưu ý cho quán</h2>
        <span className="text-neutral-50">Không bắt buộc</span>
      </div>
      <FormField
        control={control}
        name="note"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <div className="p-4 md:px-0 w-full">
                <Textarea
                  {...field}
                  className="w-full p-3 border border-neutral-30 rounded-md text-neutral-70 focus:outline-none focus:ring-2 focus:ring-primary-40 min-h-[80px] resize-none"
                  placeholder="Ghi chú đặc biệt (Ví dụ: ít đường, ít đá...)"
                  data-testid="customize-note-input"
                />
              </div>
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
}

function BottomActions({
  isLoading,
  control,
  quantity,
  isMaxQuantity,
  onIncrease,
  onDecrease,
  totalPrice,
}: {
  isLoading: boolean;
  control: any;
  quantity: number;
  isMaxQuantity: boolean;
  onIncrease: () => void;
  onDecrease: () => void;
  totalPrice: number;
}) {
  const { bottomPosition } = useBottomPosition();
  const { isNavigationVisible } = useNavigation();
  const isMobile = useIsMobile();
  return (
    <div
      className="fixed inset-x-0 z-20 w-full max-w-lg mx-auto rounded-t-xl"
      style={
        isMobile
          ? { bottom: isNavigationVisible ? bottomPosition : 0 }
          : undefined
      }
    >
      <Card className="flex gap-4 border-none !shadow-buttonDock rounded-t-xl mx-px p-4">
        {isLoading ? (
          <>
            <Skeleton className="h-10 w-24 flex-none rounded-md" />
            <Skeleton className="h-10 w-full flex-1 rounded-md" />
          </>
        ) : (
          <>
            <QuantityControl
              control={control}
              quantity={quantity}
              isMaxQuantity={isMaxQuantity}
              onIncrease={onIncrease}
              onDecrease={onDecrease}
            />
            <Button
              variant="default"
              size="lg"
              className="w-full flex-1 py-1 font-bold"
              type="submit"
              disabled={isLoading}
              data-testid="customize-add-to-cart"
            >
              <span>Thêm vào giỏ hàng:</span>
              <span data-testid="customize-total-price">
                {formatCurrency(totalPrice)}
              </span>
            </Button>
          </>
        )}
      </Card>
      {isMaxQuantity && (
        <div className="p-4 text-center text-red-50 text-sm">
          Đã đạt số lượng tối đa cho phép trong kho.
        </div>
      )}
    </div>
  );
}

interface SoloMealCarouselProps {
  soloMeals: SoloMeal[];
  currentItemId: number;
  onItemClick: (soloMeal: SoloMeal) => void;
  isLoading: boolean;
}

function SoloMealCarousel({
  soloMeals,
  currentItemId,
  onItemClick,
  isLoading,
}: SoloMealCarouselProps) {
  if (isLoading) {
    return (
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-neutral-90 mb-3">
          Món SoloMeal khác
        </h3>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex-none w-32">
              <Skeleton className="aspect-square w-full rounded-2xl mb-2" />
              <Skeleton className="h-4 w-full mb-1" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (soloMeals.length === 0) {
    return null;
  }

  return (
    <div className="mb-4 w-[calc(100vw)] bg-blue-10 -mx-4">
      <div className="flex gap-3 overflow-x-auto p-2 scrollbar-hide w-full">
        {soloMeals.map((soloMeal) => {
          const isActive = soloMeal._id === currentItemId;
          const imageSrc =
            soloMeal.image_preview_url && soloMeal.image_preview_url !== ''
              ? soloMeal.image_preview_url
              : soloMeal.image_thumb_url ||
                `${assetPrefix}/static/images/empty-store-bot.webp`;

          return (
            <button
              key={soloMeal._id}
              type="button"
              onClick={() => onItemClick(soloMeal)}
              className={cn(
                isActive ? 'border-blue-40' : 'border-none',
                'flex gap-3 border-2 rounded-lg p-2 w-52 bg-white flex-shrink-0 h-fit'
              )}
              disabled={isActive}
            >
              <div className="relative size-12 aspect-square shrink-0">
                <img
                  src={imageSrc}
                  alt={soloMeal.name}
                  className="size-full object-cover rounded-md"
                />
                {soloMeal.is_ai_image && (
                  <Badge className="absolute top-1 right-1 size-6 bg-white/50 hover:bg-white/90 text-white border-none shadow-sm p-1">
                    <Image
                      fill
                      className="p-0.5"
                      src={`${assetPrefix}/static/icons/white-ai.svg`}
                      alt={'AI'}
                    />
                  </Badge>
                )}
              </div>
              <div className="flex flex-col gap-0 flex-1 min-w-0 h-fit">
                <span className="font-normal text-base text-neutral-90 truncate">
                  {soloMeal.name}
                </span>
                <div className="flex items-end justify-end gap-1">
                  <span className="text-sm font-semibold text-[#FF6B00]">
                    {formatCurrency(soloMeal.discounted_price)}
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
